'use client';

import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useDataStore } from '../stores/dataStore';
import type { DataFile } from '../types';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { useToastActions } from '../components/ui/Toast';
import { 
  Upload, 
  Trash, 
  Eye, 
  DownloadSimple,
  MagnifyingGlass,
  Spinner,
  Plus,
  Database,
  FileCsv,
  FileXls
} from '@phosphor-icons/react';

// File Icon based on type
function FileIcon({ type }: { type: string }) {
  if (type.includes('csv')) {
    return <FileCsv className="w-5 h-5 text-emerald-500" weight="fill" />;
  }
  if (type.includes('xls') || type.includes('excel')) {
    return <FileXls className="w-5 h-5 text-blue-500" weight="fill" />;
  }
  return <FileXls className="w-5 h-5 text-zinc-400" weight="fill" />;
}

// Empty State Component
function EmptyState({ onUpload }: { onUpload: () => void }) {
  const { t } = useTranslation();
  
  return (
    <motion.div 
      className="flex flex-col items-center justify-center py-20 text-center"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 100, damping: 20 }}
    >
      <motion.div 
        className="w-20 h-20 rounded-3xl bg-zinc-100 flex items-center justify-center mb-6"
        animate={{ 
          y: [0, -8, 0],
        }}
        transition={{ 
          duration: 4, 
          repeat: Infinity, 
          ease: 'easeInOut' 
        }}
      >
        <Database className="w-10 h-10 text-zinc-300" weight="fill" />
      </motion.div>
      <h3 className="text-lg font-semibold text-zinc-900 mb-2">{t('data.noFiles')}</h3>
      <p className="text-sm text-zinc-500 max-w-[40ch] mb-6">
        {t('data.uploadPrompt')}
      </p>
      <Button
        variant="primary"
        leftIcon={<Plus className="w-4 h-4" />}
        onClick={onUpload}
      >
        {t('data.upload')}
      </Button>
    </motion.div>
  );
}

// Data Table Row
function DataTableRow({ 
  file, 
  index, 
  onPreview, 
  onDelete 
}: { 
  file: DataFile;
  index: number;
  onPreview: (file: DataFile) => void;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation();
  
  return (
    <motion.tr
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03, type: 'spring', stiffness: 100, damping: 20 }}
      className="group hover:bg-zinc-50/80 transition-colors duration-200"
    >
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-zinc-100 flex items-center justify-center group-hover:bg-white group-hover:shadow-sm transition-all">
            <FileIcon type={file.type} />
          </div>
          <div>
            <span className="text-sm font-medium text-zinc-900 block">
              {file.name}
            </span>
            <span className="text-xs text-zinc-400">
              {file.type.toUpperCase()}
            </span>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <span className="text-sm text-zinc-600 font-mono">
          {(file.size / 1024).toFixed(1)} KB
        </span>
      </td>
      <td className="px-6 py-4">
        <span className="text-sm text-zinc-600 font-mono">
          {file.rowCount.toLocaleString()}
        </span>
      </td>
      <td className="px-6 py-4">
        <span className="text-sm text-zinc-500">
          {new Date(file.uploadedAt).toLocaleDateString('zh-CN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center justify-end gap-1">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => onPreview(file)}
            className="p-2 text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
            title={t('common.preview')}
          >
            <Eye className="w-4 h-4" />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
            title={t('common.download')}
          >
            <DownloadSimple className="w-4 h-4" />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => onDelete(file.id)}
            className="p-2 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title={t('common.delete')}
          >
            <Trash className="w-4 h-4" />
          </motion.button>
        </div>
      </td>
    </motion.tr>
  );
}

// Preview Panel
function PreviewPanel({ 
  data, 
  onClose 
}: { 
  data: { 
    selectedData: { 
      name: string; 
      schema: Array<{ name: string; type: string }>;
    }; 
    previewData: Array<Record<string, unknown>>;
  }; 
  onClose: () => void;
}) {
  const { t } = useTranslation();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="mt-8"
    >
      <Card variant="elevated" className="overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Eye className="w-5 h-5 text-emerald-600" weight="fill" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-zinc-900">
                {t('common.preview')}：{data.selectedData.name}
              </h3>
              <p className="text-xs text-zinc-500">
                {t('data.showingRows', { count: Math.min(data.previewData.length, 10) })}
              </p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
          >
            <span className="sr-only">{t('common.close')}</span>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </motion.button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-50">
              <tr>
                {data.selectedData.schema.map((col) => (
                  <th
                    key={col.name}
                    className="px-6 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider"
                  >
                    <div className="flex items-center gap-2">
                      {col.name}
                      <span className="px-1.5 py-0.5 bg-zinc-200 text-zinc-600 text-[10px] rounded font-mono">
                        {col.type}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {data.previewData.slice(0, 10).map((row, index) => (
                <motion.tr 
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.03 }}
                  className="hover:bg-zinc-50/50"
                >
                  {data.selectedData.schema.map((col) => (
                    <td
                      key={col.name}
                      className="px-6 py-3 text-sm text-zinc-700 font-mono"
                    >
                      <span className="truncate max-w-[200px] block">
                        {String(row[col.name] ?? '')}
                      </span>
                    </td>
                  ))}
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </motion.div>
  );
}

export function DataManagementPage() {
  const { t } = useTranslation();
  
  const {
    dataFiles,
    selectedData,
    isLoading,
    isUploading,
    fetchDataList,
    setSelectedData,
    uploadData,
    deleteData,
    fetchPreviewData,
    previewData,
  } = useDataStore();

  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const toast = useToastActions();

  useEffect(() => {
    fetchDataList();
  }, [fetchDataList]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    await uploadData(file, file.name);
    e.target.value = '';
  };

  const handleDelete = async (id: string) => {
    const file = dataFiles.find(f => f.id === id);
    const confirmed = await confirm({
      title: t('data.deleteConfirm'),
      description: t('data.deleteConfirmDescription', { name: file?.name || t('common.thisFile') }),
      confirmText: t('common.confirmDelete'),
      cancelText: t('common.cancel'),
      type: 'danger',
    });
    
    if (confirmed) {
      await deleteData(id);
      toast.success(t('data.deleteSuccess'));
    }
  };

  const handlePreview = async (data: DataFile) => {
    setSelectedData(data);
    await fetchPreviewData(data.id, 100);
  };

  const filteredFiles = dataFiles.filter((file) =>
    file.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-zinc-50 p-8">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 100, damping: 20 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-zinc-900 tracking-tight">{t('data.title')}</h1>
              <p className="mt-2 text-zinc-500">{t('data.subtitle')}</p>
            </div>
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                variant="primary"
                size="lg"
                onClick={() => fileInputRef.current?.click()}
                isLoading={isUploading}
                leftIcon={isUploading ? undefined : <Upload className="w-5 h-5" />}
              >
                {isUploading ? t('common.uploading') : t('data.upload')}
              </Button>
            </motion.div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
        </motion.div>

        {/* Search Bar */}
        <motion.div 
          className="mb-6"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, type: 'spring', stiffness: 100, damping: 20 }}
        >
          <div className="relative max-w-md">
            <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
            <input
              type="text"
              placeholder={t('data.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="
                w-full pl-12 pr-4 py-3
                bg-white border border-zinc-200 rounded-xl
                text-sm text-zinc-900 placeholder:text-zinc-400
                focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500
                transition-all duration-200
                shadow-sm
              "
            />
          </div>
        </motion.div>

        {/* Data Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 100, damping: 20 }}
        >
          <Card className="overflow-hidden">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <Spinner className="w-8 h-8 text-emerald-500" weight="bold" />
                </motion.div>
                <p className="mt-4 text-sm text-zinc-400">{t('data.loading')}</p>
              </div>
            ) : filteredFiles.length === 0 ? (
              <EmptyState onUpload={() => fileInputRef.current?.click()} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-50 border-b border-zinc-100">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        {t('data.filename')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        {t('common.size')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        {t('data.rowCount')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        {t('data.uploadTime')}
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        {t('common.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {filteredFiles.map((file, index) => (
                      <DataTableRow
                        key={file.id}
                        file={file}
                        index={index}
                        onPreview={handlePreview}
                        onDelete={handleDelete}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </motion.div>

        {/* Preview Panel */}
        <AnimatePresence>
          {selectedData && previewData && (
            <PreviewPanel
              data={{ selectedData, previewData }}
              onClose={() => setSelectedData(null)}
            />
          )}
        </AnimatePresence>
        
        {/* Confirm Dialog */}
        {confirmDialog}
      </div>
    </div>
  );
}
